import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  InputAdornment,
  CircularProgress,
  Grid,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead';
import ArchiveIcon from '@mui/icons-material/Archive';
import UnarchiveIcon from '@mui/icons-material/Unarchive';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useTheme } from '@mui/material/styles';
import { bg } from '../../utils/darkModeAdapter';
import axios from '../../utils/axiosConfig';
import { showSuccess, showError } from '../../utils/errorHandler';

const BACKEND_URL = process.env.REACT_APP_API_URL;

const CATEGORY_CONFIG = {
  bug: { label: '不具合報告', color: 'error' },
  feature_request: { label: '機能要望', color: 'primary' },
  question: { label: '質問', color: 'success' },
  other: { label: 'その他', color: 'default' },
};

const STATUS_CONFIG = {
  unread: { label: '未読', color: 'error' },
  read: { label: '既読', color: 'info' },
  resolved: { label: '対応済み', color: 'success' },
  archived: { label: 'アーカイブ', color: 'default' },
};

export default function FeedbackManagement() {
  const theme = useTheme();
  const [feedbacks, setFeedbacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [detailDialog, setDetailDialog] = useState({ open: false, feedback: null });

  const fetchFeedbacks = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/feedbacks/admin`);
      setFeedbacks(res.data);
    } catch (err) {
      showError('フィードバックの取得に失敗しました。');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFeedbacks();
  }, []);

  const handleStatusUpdate = async (feedbackId, newStatus) => {
    try {
      await axios.patch(`${BACKEND_URL}/feedbacks/${feedbackId}/status`, { status: newStatus });
      showSuccess('ステータスを更新しました。');
      fetchFeedbacks();
    } catch (err) {
      showError('ステータスの更新に失敗しました。');
    }
  };

  const handleDelete = async (feedbackId) => {
    if (!window.confirm('このフィードバックを削除しますか？')) return;
    try {
      await axios.delete(`${BACKEND_URL}/feedbacks/${feedbackId}`);
      showSuccess('フィードバックを削除しました。');
      fetchFeedbacks();
    } catch (err) {
      showError('フィードバックの削除に失敗しました。');
    }
  };

  const filteredFeedbacks = feedbacks.filter((fb) => {
    if (filterCategory !== 'all' && fb.category !== filterCategory) return false;
    if (filterStatus !== 'all' && fb.status !== filterStatus) return false;
    if (searchText) {
      const text = searchText.toLowerCase();
      return (
        fb.subject.toLowerCase().includes(text) ||
        fb.message.toLowerCase().includes(text) ||
        fb.user_email.toLowerCase().includes(text)
      );
    }
    return true;
  });

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleString('ja-JP', { timeZone: 'Asia/Tokyo' });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* フィルター */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              size="small"
              placeholder="件名・内容・メールアドレスで検索"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <FormControl fullWidth size="small">
              <InputLabel>カテゴリ</InputLabel>
              <Select
                value={filterCategory}
                label="カテゴリ"
                onChange={(e) => setFilterCategory(e.target.value)}
              >
                <MenuItem value="all">すべて</MenuItem>
                <MenuItem value="bug">不具合報告</MenuItem>
                <MenuItem value="feature_request">機能要望</MenuItem>
                <MenuItem value="question">質問</MenuItem>
                <MenuItem value="other">その他</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} sm={3}>
            <FormControl fullWidth size="small">
              <InputLabel>ステータス</InputLabel>
              <Select
                value={filterStatus}
                label="ステータス"
                onChange={(e) => setFilterStatus(e.target.value)}
              >
                <MenuItem value="all">すべて</MenuItem>
                <MenuItem value="unread">未読</MenuItem>
                <MenuItem value="read">既読</MenuItem>
                <MenuItem value="resolved">対応済み</MenuItem>
                <MenuItem value="archived">アーカイブ</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={2}>
            <Typography variant="body2" color="text.secondary">
              {filteredFeedbacks.length} 件
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* テーブル */}
      <TableContainer component={Paper} sx={{ maxHeight: 600 }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell>ユーザー</TableCell>
              <TableCell>カテゴリ</TableCell>
              <TableCell>件名</TableCell>
              <TableCell>ステータス</TableCell>
              <TableCell>送信日時</TableCell>
              <TableCell align="center">アクション</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredFeedbacks.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    フィードバックはありません
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredFeedbacks.map((fb) => {
                const catConfig = CATEGORY_CONFIG[fb.category] || CATEGORY_CONFIG.other;
                const statusConfig = STATUS_CONFIG[fb.status] || STATUS_CONFIG.unread;
                return (
                  <TableRow
                    key={fb.id}
                    hover
                    sx={{
                      cursor: 'pointer',
                      fontWeight: fb.status === 'unread' ? 'bold' : 'normal',
                    }}
                    onClick={() => setDetailDialog({ open: true, feedback: fb })}
                  >
                    <TableCell>
                      <Typography variant="body2" sx={{ fontSize: 13 }}>
                        {fb.user_email}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip label={catConfig.label} color={catConfig.color} size="small" />
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: fb.status === 'unread' ? 600 : 400,
                          maxWidth: 300,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {fb.subject}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={statusConfig.label}
                        color={statusConfig.color}
                        size="small"
                        variant={fb.status === 'unread' ? 'filled' : 'outlined'}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontSize: 12, color: 'text.secondary' }}>
                        {formatDate(fb.created_at)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                      <Tooltip title="詳細">
                        <IconButton
                          size="small"
                          onClick={() => setDetailDialog({ open: true, feedback: fb })}
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {fb.status !== 'read' && (
                        <Tooltip title="既読にする">
                          <IconButton
                            size="small"
                            onClick={() => handleStatusUpdate(fb.id, 'read')}
                            color="info"
                          >
                            <MarkEmailReadIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {fb.status !== 'resolved' && (
                        <Tooltip title="対応済みにする">
                          <IconButton
                            size="small"
                            onClick={() => handleStatusUpdate(fb.id, 'resolved')}
                            color="success"
                          >
                            <CheckCircleIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {fb.status !== 'archived' ? (
                        <Tooltip title="アーカイブ">
                          <IconButton
                            size="small"
                            onClick={() => handleStatusUpdate(fb.id, 'archived')}
                          >
                            <ArchiveIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      ) : (
                        <Tooltip title="アーカイブ解除">
                          <IconButton
                            size="small"
                            onClick={() => handleStatusUpdate(fb.id, 'read')}
                            color="warning"
                          >
                            <UnarchiveIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="削除">
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(fb.id)}
                          color="error"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* 詳細ダイアログ */}
      <Dialog
        open={detailDialog.open}
        onClose={() => setDetailDialog({ open: false, feedback: null })}
        maxWidth="sm"
        fullWidth
      >
        {detailDialog.feedback && (
          <>
            <DialogTitle>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Chip
                  label={
                    (CATEGORY_CONFIG[detailDialog.feedback.category] || CATEGORY_CONFIG.other).label
                  }
                  color={
                    (CATEGORY_CONFIG[detailDialog.feedback.category] || CATEGORY_CONFIG.other).color
                  }
                  size="small"
                />
                <Chip
                  label={
                    (STATUS_CONFIG[detailDialog.feedback.status] || STATUS_CONFIG.unread).label
                  }
                  color={
                    (STATUS_CONFIG[detailDialog.feedback.status] || STATUS_CONFIG.unread).color
                  }
                  size="small"
                  variant="outlined"
                />
              </Box>
              <Typography variant="h6" sx={{ mt: 1 }}>
                {detailDialog.feedback.subject}
              </Typography>
            </DialogTitle>
            <DialogContent>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                送信者: {detailDialog.feedback.user_email} |{' '}
                {formatDate(detailDialog.feedback.created_at)}
              </Typography>
              <Paper
                variant="outlined"
                sx={{ p: 2, backgroundColor: bg(theme).subtle, whiteSpace: 'pre-wrap' }}
              >
                <Typography variant="body1">{detailDialog.feedback.message}</Typography>
              </Paper>
            </DialogContent>
            <DialogActions>
              {detailDialog.feedback.status !== 'read' && (
                <Button
                  onClick={() => {
                    handleStatusUpdate(detailDialog.feedback.id, 'read');
                    setDetailDialog({ open: false, feedback: null });
                  }}
                  startIcon={<MarkEmailReadIcon />}
                >
                  既読にする
                </Button>
              )}
              {detailDialog.feedback.status !== 'resolved' && (
                <Button
                  onClick={() => {
                    handleStatusUpdate(detailDialog.feedback.id, 'resolved');
                    setDetailDialog({ open: false, feedback: null });
                  }}
                  startIcon={<CheckCircleIcon />}
                  color="success"
                >
                  対応済み
                </Button>
              )}
              {detailDialog.feedback.status !== 'archived' ? (
                <Button
                  onClick={() => {
                    handleStatusUpdate(detailDialog.feedback.id, 'archived');
                    setDetailDialog({ open: false, feedback: null });
                  }}
                  startIcon={<ArchiveIcon />}
                >
                  アーカイブ
                </Button>
              ) : (
                <Button
                  onClick={() => {
                    handleStatusUpdate(detailDialog.feedback.id, 'read');
                    setDetailDialog({ open: false, feedback: null });
                  }}
                  startIcon={<UnarchiveIcon />}
                  color="warning"
                >
                  アーカイブ解除
                </Button>
              )}
              <Button onClick={() => setDetailDialog({ open: false, feedback: null })}>
                閉じる
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
}
